import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '../lib/auth';
import { useState, useEffect } from 'react';
import { useKitchen } from '../hooks/useKitchen';
import { usePantry } from '../hooks/useShopping';
import { useToast } from '../hooks/useToast';
import { useJoinKitchenModal } from '../hooks/useJoinKitchenModal';
import { ToastContainer } from '../components/Toast';
import { JoinKitchenForm } from '../components/JoinKitchenForm';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ProtectedRoute } from '../components/auth';

export const Route = createFileRoute('/kitchen')({
  component: ProtectedKitchenPage,
});

function ProtectedKitchenPage() {
  return (
    <ProtectedRoute>
      <KitchenPage />
    </ProtectedRoute>
  );
}

type Tab = 'general' | 'pantry';

function KitchenPage() {
  const { isSignedIn, isLoaded } = useAuth();
  const navigate = useNavigate();
  const { toasts, removeToast, success, error: showError } = useToast();
  const { isOpen: isJoinModalOpen, open: openJoinModal, close: closeJoinModal } = useJoinKitchenModal();
  const [activeTab, setActiveTab] = useState<Tab>('general');

  // Redirect if not signed in
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate({ to: '/login' });
    }
  }, [isLoaded, isSignedIn, navigate]);

  const {
    kitchen,
    members,
    isLoading,
    createKitchen,
    updateKitchen,
    createInvite,
    leaveKitchen,
    isUpdatingKitchen,
    isCreatingInvite,
    isLeavingKitchen,
  } = useKitchen();

  const {
    items: pantryItems,
    addToPantry,
    removeFromPantry,
    setupDefaultPantry,
    isLoading: isPantryLoading,
  } = usePantry();

  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [kitchenName, setKitchenName] = useState('');
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [isAutoCreating, setIsAutoCreating] = useState(false);
  const [newPantryItem, setNewPantryItem] = useState('');
  const [pantrySearch, setPantrySearch] = useState('');

  // Auto-create kitchen if none exists
  useEffect(() => {
    if (isLoaded && isSignedIn && !isLoading && kitchen === null && !isAutoCreating) {
      setIsAutoCreating(true);
      createKitchen()
        .then(() => {
          success('Welcome to your new kitchen! 🎉');
          // Setup default pantry items for new kitchen
          return setupDefaultPantry();
        })
        .catch((err: any) => {
          // Silent fail - will show create/join UI
          console.log('Auto-create failed:', err?.message);
        })
        .finally(() => setIsAutoCreating(false));
    }
  }, [isLoaded, isSignedIn, isLoading, kitchen, createKitchen, success, isAutoCreating, setupDefaultPantry]);

  useEffect(() => {
    if (kitchen?.name) {
      setKitchenName(kitchen.name);
    }
  }, [kitchen?.name]);

  const handleCreateInvite = async () => {
    try {
      const result = await createInvite();
      const url = `${window.location.origin}/join/${result.token}`;
      setInviteUrl(url);
      setInviteToken(result.token);
      success('Invite link created!');
    } catch {
      showError('Failed to create invite link');
    }
  };

  const handleCopyInvite = async () => {
    const textToCopy = inviteUrl || inviteToken;
    if (!textToCopy) return;

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    const textToShare = inviteUrl || inviteToken;
    if (!textToShare) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join my kitchen on MiseBook',
          text: 'Join me on MiseBook to plan meals together!',
          url: textToShare,
        });
      } catch {
        handleCopyInvite();
      }
    } else {
      handleCopyInvite();
    }
  };

  const handleUpdateName = async () => {
    const trimmedName = kitchenName.trim();
    if (!trimmedName || trimmedName === kitchen?.name) {
      setIsEditingName(false);
      setKitchenName(kitchen?.name || '');
      return;
    }

    try {
      await updateKitchen({ name: trimmedName });
      setIsEditingName(false);
      success('Kitchen name updated!');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update name';
      showError(message);
      setKitchenName(kitchen?.name || '');
    }
  };

  const handleLeaveKitchen = async () => {
    try {
      await leaveKitchen();
      setShowLeaveConfirm(false);
      success('Left kitchen successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to leave kitchen';
      showError(message);
      setShowLeaveConfirm(false);
    }
  };

  const handleAddPantryItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPantryItem.trim()) return;

    try {
      await addToPantry(newPantryItem.trim());
      setNewPantryItem('');
      success(`Added ${newPantryItem.trim()} to pantry`);
    } catch (err) {
      showError('Failed to add item');
    }
  };

  const handleRemovePantryItem = async (id: string, name: string) => {
    try {
      await removeFromPantry(id);
      success(`Removed ${name} from pantry`);
    } catch (err) {
      showError('Failed to remove item');
    }
  };

  const isCurrentUserOwner = kitchen?.role === 'owner';

  if (!isLoaded || isLoading || isAutoCreating) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-stone">{isAutoCreating ? 'Creating your kitchen...' : 'Loading...'}</div>
      </div>
    );
  }

  if (!isSignedIn) {
    return null;
  }

  // No kitchen — show create/join options (fallback if auto-create failed)
  if (!kitchen) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="font-display text-2xl font-semibold text-ink mb-6">
          Your Kitchen
        </h1>

        {/* Create Kitchen */}
        <div className="bg-white rounded-2xl border border-mist p-6 mb-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-coral-100 flex items-center justify-center flex-shrink-0">
              <span className="text-3xl">🏠</span>
            </div>
            <div>
              <h2 className="font-display text-xl font-semibold text-ink">
                Create Your Kitchen
              </h2>
              <p className="text-stone text-sm mt-1">
                A kitchen is your shared space for recipes, meal plans, and shopping lists.
              </p>
            </div>
          </div>

          <button
            onClick={() => createKitchen().then(() => success('Kitchen created!'))}
            className="btn btn-primary w-full"
          >
            Create Kitchen
          </button>
        </div>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-mist" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-snow px-4 text-sm text-stone">or</span>
          </div>
        </div>

        {/* Join Kitchen */}
        <div className="bg-white rounded-2xl border border-mist p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
              <span className="text-2xl">🔗</span>
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold text-ink">
                Join a Kitchen
              </h2>
              <p className="text-stone text-sm">
                Have an invite code? Join someone else's kitchen.
              </p>
            </div>
          </div>

          {!isJoinModalOpen ? (
            <button onClick={openJoinModal} className="btn btn-secondary w-full">
              Join with Invite Code
            </button>
          ) : (
            <div className="bg-sand/30 rounded-xl p-4">
              <JoinKitchenForm
                onSuccess={() => {
                  closeJoinModal();
                  window.location.reload();
                }}
                onCancel={closeJoinModal}
              />
            </div>
          )}
        </div>

        <ToastContainer toasts={toasts} onRemove={removeToast} />
      </div>
    );
  }

  // Has kitchen — full UI with tabs
  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="font-display text-2xl font-semibold text-ink mb-6">
        Your Kitchen
      </h1>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-mist">
        <button
          onClick={() => setActiveTab('general')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            activeTab === 'general'
              ? 'border-coral-500 text-coral-600'
              : 'border-transparent text-stone hover:text-ink'
          }`}
        >
          🏠 General
        </button>
        <button
          onClick={() => setActiveTab('pantry')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            activeTab === 'pantry'
              ? 'border-coral-500 text-coral-600'
              : 'border-transparent text-stone hover:text-ink'
          }`}
        >
          🥡 Pantry ({pantryItems.length})
        </button>
      </div>

      {activeTab === 'general' && (
        <>
          {/* Kitchen Info */}
          <div className="bg-white rounded-2xl border border-mist p-6 mb-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl bg-coral-100 flex items-center justify-center flex-shrink-0">
                <span className="text-3xl">🏠</span>
              </div>
              <div className="flex-1 min-w-0">
                {isEditingName ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={kitchenName}
                      onChange={(e) => setKitchenName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdateName();
                        if (e.key === 'Escape') {
                          setIsEditingName(false);
                          setKitchenName(kitchen?.name || '');
                        }
                      }}
                      className="w-full px-3 py-2 bg-sand rounded-lg text-ink font-display text-lg focus:outline-none focus:ring-2 focus:ring-coral-500"
                      autoFocus
                      maxLength={100}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleUpdateName}
                        disabled={isUpdatingKitchen || !kitchenName.trim()}
                        className="text-sm px-3 py-1 bg-coral-600 text-white rounded-lg hover:bg-coral-700 disabled:opacity-50"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingName(false);
                          setKitchenName(kitchen?.name || '');
                        }}
                        className="text-sm px-3 py-1 text-stone hover:text-ink"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <h2 className="font-display text-xl font-semibold text-ink truncate">
                        {kitchen.name}
                      </h2>
                      {isCurrentUserOwner && (
                        <button
                          onClick={() => setIsEditingName(true)}
                          className="text-stone hover:text-ink p-1"
                          title="Edit name"
                        >
                          ✏️
                        </button>
                      )}
                    </div>
                    <p className="text-stone text-sm">
                      {members.length} member{members.length !== 1 ? 's' : ''}
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Members List */}
            <div className="border-t border-mist pt-4">
              <h3 className="text-sm font-medium text-stone uppercase tracking-wide mb-3">
                Members
              </h3>
              <div className="space-y-2">
                {members.length === 0 && (
                  <p className="text-stone text-sm py-2">Just you so far. Invite someone below!</p>
                )}
                {members.map((member) => (
                  <div
                    key={member.userId}
                    className="flex items-center gap-3 p-3 rounded-lg bg-sand/50"
                  >
                    <div className="w-10 h-10 rounded-full bg-coral-200 flex items-center justify-center text-coral-700 font-medium overflow-hidden">
                      {member.imageUrl ? (
                        <img
                          src={member.imageUrl}
                          alt={member.name || 'Member'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        member.name?.[0]?.toUpperCase() || '?'
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-ink truncate">
                        {member.name || 'Unknown'}
                        {member.role === 'owner' && (
                          <span className="ml-2 text-xs bg-coral-100 text-coral-700 px-2 py-0.5 rounded-full">
                            Owner
                          </span>
                        )}
                      </div>
                      {member.email && (
                        <div className="text-xs text-stone truncate">{member.email}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Invite Section */}
          <div className="bg-white rounded-2xl border border-mist p-6 mb-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-coral-100 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">👥</span>
              </div>
              <div>
                <h2 className="font-display text-lg font-semibold text-ink">
                  Invite Someone
                </h2>
                <p className="text-stone text-sm">
                  Share your kitchen with your partner, roommate, or family.
                </p>
              </div>
            </div>

            {!inviteUrl ? (
              <button
                onClick={handleCreateInvite}
                disabled={isCreatingInvite}
                className="btn btn-primary w-full"
              >
                {isCreatingInvite ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating invite...
                  </span>
                ) : (
                  'Create Invite Link'
                )}
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inviteUrl}
                    readOnly
                    className="flex-1 px-4 py-3 bg-sand rounded-lg text-ink text-sm font-mono truncate"
                  />
                  <button
                    onClick={handleCopyInvite}
                    className="btn btn-secondary px-4 flex-shrink-0"
                  >
                    {copied ? '✓ Copied!' : 'Copy'}
                  </button>
                </div>

                <button
                  onClick={handleShare}
                  className="btn btn-primary w-full flex items-center justify-center gap-2"
                >
                  <span>📤</span>
                  Share Invite Link
                </button>

                {inviteToken && (
                  <div className="p-3 bg-sand/50 rounded-lg">
                    <p className="text-xs text-stone mb-2">Or share this code:</p>
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(inviteToken);
                          setCodeCopied(true);
                          setTimeout(() => setCodeCopied(false), 2000);
                        } catch {
                          const textarea = document.createElement('textarea');
                          textarea.value = inviteToken;
                          document.body.appendChild(textarea);
                          textarea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textarea);
                          setCodeCopied(true);
                          setTimeout(() => setCodeCopied(false), 2000);
                        }
                      }}
                      className="group w-full block text-left text-sm font-mono bg-white px-3 py-2 rounded border border-mist hover:border-coral-300 hover:bg-coral-50 transition-colors cursor-pointer"
                    >
                      <code className="break-all">{inviteToken}</code>
                      <div className="text-xs text-stone group-hover:text-coral-600 mt-1 flex items-center gap-1">
                        {codeCopied ? (
                          <>
                            <span>✓</span>
                            <span>Copied!</span>
                          </>
                        ) : (
                          <>
                            <span>📋</span>
                            <span className="hidden sm:inline">Click to copy</span>
                          </>
                        )}
                      </div>
                    </button>
                  </div>
                )}

                <p className="text-xs text-stone text-center">
                  Link expires in 7 days and can only be used once.
                </p>
              </div>
            )}
          </div>

          {/* Join Another Kitchen */}
          <div className="bg-white rounded-2xl border border-mist p-6 mb-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🔗</span>
              </div>
              <div>
                <h2 className="font-display text-lg font-semibold text-ink">
                  Join Another Kitchen
                </h2>
                <p className="text-stone text-sm">
                  Have an invite code? Join a different kitchen. You'll leave your current kitchen.
                </p>
              </div>
            </div>

            {!isJoinModalOpen ? (
              <button onClick={openJoinModal} className="btn btn-secondary w-full">
                Join with Invite Code
              </button>
            ) : (
              <div className="bg-sand/30 rounded-xl p-4">
                <JoinKitchenForm
                  onSuccess={() => {
                    closeJoinModal();
                    window.location.reload();
                  }}
                  onCancel={closeJoinModal}
                />
              </div>
            )}
          </div>

          {/* Leave Kitchen (non-owners) */}
          {!isCurrentUserOwner && (
            <div className="bg-white rounded-2xl border border-mist p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">🚪</span>
                </div>
                <div>
                  <h2 className="font-display text-lg font-semibold text-ink">
                    Leave Kitchen
                  </h2>
                  <p className="text-stone text-sm">
                    You'll lose access to shared recipes and meal plans.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowLeaveConfirm(true)}
                disabled={isLeavingKitchen}
                className="btn btn-secondary w-full border-rose-200 text-rose-700 hover:bg-rose-50"
              >
                {isLeavingKitchen ? 'Leaving...' : 'Leave Kitchen'}
              </button>
            </div>
          )}
        </>
      )}

      {activeTab === 'pantry' && (
        <div className="bg-white rounded-2xl border border-mist p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
              <span className="text-2xl">🥡</span>
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold text-ink">
                Kitchen Pantry
              </h2>
              <p className="text-stone text-sm">
                Items you always have on hand. These are excluded from your shopping list.
              </p>
            </div>
          </div>

          {/* Add new pantry item */}
          <form onSubmit={handleAddPantryItem} className="flex gap-2 mb-6">
            <input
              type="text"
              value={newPantryItem}
              onChange={(e) => setNewPantryItem(e.target.value)}
              placeholder="Add item to pantry (e.g., Olive oil)"
              className="input flex-1"
            />
            <button
              type="submit"
              disabled={!newPantryItem.trim()}
              className="btn btn-primary"
            >
              Add
            </button>
          </form>

          {/* Search pantry items */}
          {pantryItems.length > 0 && (
            <div className="mb-4">
              <input
                type="text"
                value={pantrySearch}
                onChange={(e) => setPantrySearch(e.target.value)}
                placeholder="Search pantry items..."
                className="input w-full"
              />
            </div>
          )}
          {/* Empty state - show Add Common Items button */}
          {!isPantryLoading && pantryItems.length === 0 && (
            <div className="mb-6">
              <div className="text-center py-8 bg-sand/30 rounded-xl mb-4">
                <p className="text-stone mb-4">Your pantry is empty.</p>
                <button
                  onClick={() => setupDefaultPantry().then(() => success("Added common pantry items"))}
                  className="btn btn-primary"
                >
                  🥡 Add Common Items
                </button>
              </div>
              <p className="text-xs text-stone text-center">
                Adds salt, pepper, oil, sugar, flour, rice, pasta, garlic, onions, butter, milk, eggs, and more.
              </p>
            </div>
          )}

          {/* Pantry items list */}
          {isPantryLoading ? (
            <div className="text-center py-8 text-stone">Loading pantry...</div>
          ) : pantryItems.length === 0 ? (
            null  // Empty state handled above
          ) : (
            <div className="space-y-2">
              {pantryItems.filter(item => item.ingredientName.toLowerCase().includes(pantrySearch.toLowerCase())).map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-sand/30 group"
                >
                  <span className="text-ink">{item.ingredientName}</span>
                  <button
                    onClick={() => handleRemovePantryItem(item.id, item.ingredientName)}
                    className="text-stone hover:text-rose-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove from pantry"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={showLeaveConfirm}
        title="Leave Kitchen?"
        message="You'll lose access to all shared recipes, meal plans, and shopping lists."
        confirmLabel="Leave Kitchen"
        cancelLabel="Stay"
        isDanger
        isLoading={isLeavingKitchen}
        onConfirm={handleLeaveKitchen}
        onCancel={() => setShowLeaveConfirm(false)}
      />

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
